"use client";

import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "./index";

// Custom hook to prevent body scroll when sheet is open
function useBodyScrollLock(isLocked: boolean) {
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const originalStyle = window.getComputedStyle(document.body).overflow;

    if (isLocked) {
      // Prevent scrolling on the body
      document.body.style.overflow = "hidden";
      // Also prevent scrolling on the html element for iOS Safari
      document.documentElement.style.overflow = "hidden";
    } else {
      // Restore original overflow
      document.body.style.overflow = originalStyle;
      document.documentElement.style.overflow = originalStyle;
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
      document.documentElement.style.overflow = originalStyle;
    };
  }, [isLocked]);
}

// Custom hook to detect mobile devices
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => {
    // Initialize with a proper check on first render
    if (typeof window === "undefined") return false;

    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

    return isMobileUserAgent || (isTouchDevice && isSmallScreen);
  });

  React.useEffect(() => {
    // Only listen to resize events if we need to check screen size
    // Mobile user agents don't need resize listeners
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );

    // If it's definitely a mobile user agent, no need to listen to resize
    if (isMobileUserAgent) {
      setIsMobile(true);
      return;
    }

    const checkIsMobile = () => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

      const newIsMobile = isTouchDevice && isSmallScreen;

      // Only update state if the value actually changed
      setIsMobile((prevIsMobile) => {
        if (prevIsMobile !== newIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };

    // Use a debounced resize handler to prevent excessive re-renders
    let timeoutId: NodeJS.Timeout;
    const debouncedCheckIsMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsMobile, 250); // Increased debounce time
    };

    // Check on resize with debouncing
    window.addEventListener("resize", debouncedCheckIsMobile);

    return () => {
      window.removeEventListener("resize", debouncedCheckIsMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  return isMobile;
}

// Sheet context for managing state
interface SheetContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  hideOverlay?: boolean; // <-- add this
}

const SheetContext = React.createContext<SheetContextType | undefined>(
  undefined,
);

const useSheetContext = () => {
  const context = React.useContext(SheetContext);
  if (context === undefined) {
    throw new Error("useSheetContext must be used within a Sheet provider");
  }
  return context;
};

// Create a cross icon component to replace Radix's Cross2Icon
const CrossIcon = ({ className }: { className?: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    ></path>
  </svg>
);

interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  hideOverlay?: boolean; // <-- add this
}

const Sheet = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  hideOverlay = false, // <-- add this
}: SheetProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setUncontrolledOpen(value);
      }
    },
    [isControlled, onOpenChange],
  );

  return (
    <SheetContext.Provider value={{ open, setOpen, triggerRef, hideOverlay }}>
      {children}
    </SheetContext.Provider>
  );
};

// Updated type definition without asChild
type SheetTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: React.ReactNode;
};

const SheetTrigger = React.forwardRef<HTMLButtonElement, SheetTriggerProps>(
  ({ children, icon, className, ...props }, forwardedRef) => {
    const { setOpen, triggerRef } = useSheetContext();

    // Create a combined ref that works with forwardRef pattern
    // while respecting the read-only nature of ref.current
    const combinedRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        // Update the internal triggerRef without directly assigning to .current
        if (node) {
          // Use internal React method to set ref value safely
          // This is a workaround for the read-only nature of ref.current
          Object.defineProperty(triggerRef, "current", {
            value: node,
            writable: true,
          });
        }

        // Forward the ref if needed
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          // Use same technique for forwarded ref
          Object.defineProperty(forwardedRef, "current", {
            value: node,
            writable: true,
          });
        }
      },
      [forwardedRef, triggerRef],
    );

    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        ref={combinedRef}
        className={cn("fluid z-30", className)}
        data-fluid-trigger="true"
        {...props}
      >
        {icon ?? children}
      </button>
    );
  },
);
SheetTrigger.displayName = "SheetTrigger";

type SheetCloseProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const SheetClose = React.forwardRef<HTMLButtonElement, SheetCloseProps>(
  ({ children, ...props }, ref) => {
    const { setOpen } = useSheetContext();

    return (
      <button type="button" onClick={() => setOpen(false)} ref={ref} {...props}>
        {children}
      </button>
    );
  },
);
SheetClose.displayName = "SheetClose";

// Custom portal implementation that works with Shadow DOM
const SheetPortal = ({
  children,
  containerRoot,
}: {
  children: React.ReactNode;
  containerRoot?: ShadowRoot | Element;
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setMounted(true);

    // Create portal container
    const div = document.createElement("div");
    div.setAttribute("data-sheet-portal", "");

    // If containerRoot is provided, use it directly
    if (containerRoot) {
      // Find the shadow-container inside the containerRoot if it's a ShadowRoot
      if (containerRoot instanceof ShadowRoot) {
        const shadowContainer =
          containerRoot.querySelector(".shadow-container");
        if (shadowContainer) {
          // Append to the shadow-container when possible for better style inheritance
          shadowContainer.appendChild(div);
        } else {
          // Fallback to appending directly to the shadow root
          containerRoot.appendChild(div);
        }
      } else {
        // For non-ShadowRoot elements, append directly
        containerRoot.appendChild(div);
      }
    } else {
      // If no containerRoot provided, use document.body as fallback
      document.body.appendChild(div);
    }

    setPortalContainer(div);

    // Cleanup function
    return () => {
      const parent = div.parentNode;
      if (parent) {
        parent.removeChild(div);
      }
    };
  }, [containerRoot]);

  if (!mounted || !portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
};
SheetPortal.displayName = "SheetPortal";

type SheetOverlayProps = React.HTMLAttributes<HTMLDivElement>;

const SheetOverlay = React.forwardRef<HTMLDivElement, SheetOverlayProps>(
  ({ className, ...props }, ref) => {
    const { open, setOpen } = useSheetContext();
    const isMobile = useIsMobile();

    React.useEffect(() => {
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
        }
      };

      window.addEventListener("keydown", handleEscapeKey);

      return () => {
        window.removeEventListener("keydown", handleEscapeKey);
      };
    }, [open, setOpen]);

    if (!open) return null;

    return (
      <div
        className={cn(
          "fluid fixed inset-0 z-40 transition-opacity",
          "opacity-100",
          // Only apply dark background on non-mobile devices
          !isMobile && "bg-black/50",
          className,
        )}
        onClick={() => setOpen(false)}
        ref={ref}
        {...props}
      />
    );
  },
);
SheetOverlay.displayName = "SheetOverlay";

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "right" | "bottom" | "left";
  containerRoot?: ShadowRoot | Element;
  alwaysOpen?: boolean;
}

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  (
    {
      className,
      children,
      side = "right",
      containerRoot,
      alwaysOpen = false,
      ...props
    },
    ref,
  ) => {
    const { open, setOpen, hideOverlay } = useSheetContext();
    const [isVisible, setIsVisible] = React.useState(false);
    const [animationReady, setAnimationReady] = React.useState(false);
    const animationRef = React.useRef<number | null>(null);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Detect if we're on mobile and apply scroll lock
    const isMobile = useIsMobile();
    useBodyScrollLock(isMobile && open && !hideOverlay); // <-- only lock if overlay is shown

    // Touch/swipe handling state
    const [touchStart, setTouchStart] = React.useState<{
      x: number;
      y: number;
    } | null>(null);
    const [touchCurrent, setTouchCurrent] = React.useState<{
      x: number;
      y: number;
    } | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Combine refs
    const combinedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref && typeof ref === "object") {
          ref.current = node;
        }
      },
      [ref],
    );

    // Touch event handlers for swipe-to-close
    const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        setTouchStart({ x: touch.clientX, y: touch.clientY });
        setTouchCurrent({ x: touch.clientX, y: touch.clientY });
        setIsDragging(false);
      }
    }, []);

    const handleTouchMove = React.useCallback(
      (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch && touchStart) {
          setTouchCurrent({ x: touch.clientX, y: touch.clientY });

          const deltaX = touch.clientX - touchStart.x;
          const deltaY = touch.clientY - touchStart.y;

          // Determine if this is a valid swipe gesture based on sheet side
          let isValidSwipe = false;
          const minSwipeDistance = 10; // Minimum distance to start considering it a swipe

          switch (side) {
            case "right":
              isValidSwipe =
                deltaX > minSwipeDistance &&
                Math.abs(deltaY) < Math.abs(deltaX);
              break;
            case "left":
              isValidSwipe =
                deltaX < -minSwipeDistance &&
                Math.abs(deltaY) < Math.abs(deltaX);
              break;
            case "top":
              isValidSwipe =
                deltaY < -minSwipeDistance &&
                Math.abs(deltaX) < Math.abs(deltaY);
              break;
            case "bottom":
              isValidSwipe =
                deltaY > minSwipeDistance &&
                Math.abs(deltaX) < Math.abs(deltaY);
              break;
          }

          if (isValidSwipe) {
            setIsDragging(true);

            // Apply transform to follow the finger
            if (contentRef.current) {
              let transform = "";

              switch (side) {
                case "right":
                  transform = `translateX(${Math.max(0, deltaX)}px)`;
                  break;
                case "left":
                  transform = `translateX(${Math.min(0, deltaX)}px)`;
                  break;
                case "top":
                  transform = `translateY(${Math.min(0, deltaY)}px)`;
                  break;
                case "bottom":
                  transform = `translateY(${Math.max(0, deltaY)}px)`;
                  break;
              }

              contentRef.current.style.transform = transform;
              contentRef.current.style.transition = "none";
            }
          }
        }
      },
      [touchStart, side],
    );

    const handleTouchEnd = React.useCallback(() => {
      if (touchStart && touchCurrent && isDragging) {
        const deltaX = touchCurrent.x - touchStart.x;
        const deltaY = touchCurrent.y - touchStart.y;
        const swipeThreshold = 100; // Distance needed to trigger close

        let shouldClose = false;

        switch (side) {
          case "right":
            shouldClose = deltaX > swipeThreshold;
            break;
          case "left":
            shouldClose = deltaX < -swipeThreshold;
            break;
          case "top":
            shouldClose = deltaY < -swipeThreshold;
            break;
          case "bottom":
            shouldClose = deltaY > swipeThreshold;
            break;
        }

        if (shouldClose) {
          setOpen(false);
        }

        // Reset transform and transition
        if (contentRef.current) {
          contentRef.current.style.transform = "";
          contentRef.current.style.transition = "";
        }
      }

      setTouchStart(null);
      setTouchCurrent(null);
      setIsDragging(false);
    }, [touchStart, touchCurrent, isDragging, side, setOpen]);

    React.useEffect(() => {
      if (open) {
        // Clear any existing timeouts/animations
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // Make the component visible immediately
        setIsVisible(true);
        setAnimationReady(false);

        // Use both RAF and a small timeout for better reliability
        animationRef.current = requestAnimationFrame(() => {
          // Add a fixed delay that's long enough to ensure the browser has rendered
          // but short enough that users won't notice (20ms is typically unnoticeable)
          timeoutRef.current = setTimeout(() => {
            setAnimationReady(true);
          }, 20);
        });
      } else {
        // For closing, immediately start the exit animation
        setAnimationReady(false);

        // Delay removal from DOM to allow exit animation to complete
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 300);
      }

      // Clean up all timers and animation frames
      return () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [open]);

    // Don't render anything if not visible
    if (!isVisible && !open) return null;

    const contentClasses = {
      "inset-y-0 right-0 h-full w-3/4 border-l": side === "right",
      "inset-y-0 left-0 h-full w-3/4 border-r": side === "left",
      "inset-x-0 top-0 h-auto w-full border-b": side === "top",
      "inset-x-0 bottom-0 h-auto w-full border-t": side === "bottom",
    };

    // Force the initial off-screen position until animationReady is true
    const transformClasses = {
      "translate-x-full": side === "right" && (!open || !animationReady),
      "-translate-x-full": side === "left" && (!open || !animationReady),
      "-translate-y-full": side === "top" && (!open || !animationReady),
      "translate-y-full": side === "bottom" && (!open || !animationReady),
      "translate-x-0 translate-y-0": open && animationReady,
    };

    return (
      <SheetPortal containerRoot={containerRoot}>
        {/* Render content before overlay in the DOM */}
        <div
          ref={combinedRef}
          aria-modal="true"
          role="dialog"
          data-state={open ? "open" : "closed"}
          data-animation-ready={animationReady ? "true" : "false"}
          className={cn(
            "fluid fixed z-[100] gap-4 bg-white p-3 shadow-lg transition-all duration-300 ease-in-out",
            contentClasses,
            transformClasses,
            "sm:max-w-sm md:max-w-md",
            className,
          )}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          {...props}
        >
          {children}
          {!alwaysOpen && (
            <SheetClose className="absolute right-3 top-4 flex h-10 w-10 items-center justify-center rounded-full border-none bg-muted p-0 opacity-70 ring-offset-background transition-colors hover:bg-muted/90 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none sm:h-8 sm:w-8">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/90 sm:h-8 sm:w-8">
                <CrossIcon className="h-[20px] w-[20px] text-foreground" />
              </div>
              <span className="sr-only">Close</span>
            </SheetClose>
          )}
        </div>
        {!hideOverlay && <SheetOverlay />}{" "}
        {/* <-- only render overlay if not hidden */}
      </SheetPortal>
    );
  },
);
SheetContent.displayName = "SheetContent";

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "-mx-5 -my-1 flex flex-col-reverse justify-center bg-muted p-2 text-center text-xs text-foreground sm:flex-row sm:space-x-2",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

type SheetTitleProps = React.HTMLAttributes<HTMLHeadingElement>;

const SheetTitle = React.forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn("text-lg font-semibold text-foreground", className)}
      {...props}
    />
  ),
);
SheetTitle.displayName = "SheetTitle";

type SheetDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>;

const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  SheetDescriptionProps
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  useBodyScrollLock,
  useIsMobile,
  type SheetContentProps,
};
